<script lang="ts">
  import { onMount } from 'svelte';
  import { api, ApiError } from '../lib/api';
  import { app } from '../lib/stores.svelte';

  let { onLoggedIn }: { onLoggedIn: () => void } = $props();

  let devUsers = $state<{ id: string; username: string; display_name: string }[]>([]);
  let step = $state<'password' | 'code'>('password');
  let email = $state('');
  let password = $state('');
  let code = $state('');
  let challenge = '';
  let error = $state('');
  let busy = $state(false);

  const dev = $derived(app.info?.auth_mode === 'dev');

  onMount(async () => {
    if (dev) devUsers = await api.devUsers().catch(() => []);
  });

  async function attempt(action: () => Promise<void>) {
    error = '';
    busy = true;
    try {
      await action();
    } catch (e) {
      error = e instanceof ApiError ? e.message : 'Une erreur est survenue';
    } finally {
      busy = false;
    }
  }

  const submitPassword = () =>
    attempt(async () => {
      challenge = (await api.login(email, password)).challenge;
      password = '';
      step = 'code';
    });

  const submitCode = () =>
    attempt(async () => {
      await api.totp(challenge, code.trim());
      onLoggedIn();
    });

  const devLogin = (username: string) =>
    attempt(async () => {
      await api.devLogin(username);
      onLoggedIn();
    });
</script>

<main class="page">
  <section class="card" aria-labelledby="login-title">
    <div class="brand">
      <span class="logo" aria-hidden="true">{(app.info?.app_name ?? 'M').charAt(0)}</span>
      <h1 id="login-title">{app.info?.app_name ?? 'Messagerie'}</h1>
    </div>

    {#if dev}
      <p class="hint">Mode développement : choisissez un compte, sans mot de passe.</p>
      <ul class="dev-users">
        {#each devUsers as user (user.id)}
          <li>
            <button type="button" class="btn" disabled={busy} onclick={() => devLogin(user.username)}>
              {user.display_name} <span class="username">@{user.username}</span>
            </button>
          </li>
        {:else}
          <li class="hint">Aucun compte. Créez-en un : <code>messagerie user create</code>.</li>
        {/each}
      </ul>
    {:else if step === 'password'}
      <form
        onsubmit={(e) => {
          e.preventDefault();
          void submitPassword();
        }}
      >
        <div class="field">
          <label for="email">Adresse e-mail</label>
          <input id="email" class="input" type="email" autocomplete="username" required bind:value={email} />
        </div>
        <div class="field">
          <label for="password">Mot de passe</label>
          <input
            id="password"
            class="input"
            type="password"
            autocomplete="current-password"
            required
            bind:value={password}
          />
        </div>
        <button type="submit" class="btn primary" disabled={busy}>Continuer</button>
      </form>
    {:else}
      <form
        onsubmit={(e) => {
          e.preventDefault();
          void submitCode();
        }}
      >
        <div class="field">
          <label for="code">Code à 6 chiffres de votre application d'authentification</label>
          <input
            id="code"
            class="input code"
            inputmode="numeric"
            autocomplete="one-time-code"
            required
            bind:value={code}
          />
          <span class="hint">Ou l'un de vos codes de secours.</span>
        </div>
        <button type="submit" class="btn primary" disabled={busy}>Se connecter</button>
        <button type="button" class="link" onclick={() => ((step = 'password'), (code = ''))}>
          Revenir au mot de passe
        </button>
      </form>
    {/if}

    {#if error}<p class="error" role="alert">{error}</p>{/if}
  </section>
</main>

<style>
  .page {
    min-height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    background: var(--bg-page);
  }
  .card {
    width: 100%;
    max-width: 400px;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-l);
    padding: 32px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .brand {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .logo {
    width: 36px;
    height: 36px;
    border-radius: var(--radius-m);
    background: var(--accent);
    color: #ffffff;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  h1 {
    margin: 0;
    font-size: 20px;
    line-height: 28px;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .code {
    font-size: 20px;
    letter-spacing: 0.2em;
  }
  .hint {
    margin: 0;
    color: var(--muted);
    font-size: 13px;
  }
  .dev-users {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .dev-users .btn {
    width: 100%;
    justify-content: flex-start;
  }
  .username {
    color: var(--muted);
    font-weight: 400;
  }
  .link {
    border: 0;
    background: none;
    color: var(--accent-text);
    padding: 0;
    align-self: flex-start;
  }
</style>
